import { ChangeDetectionStrategy, Component, computed } from '@angular/core';
import { Router } from '@angular/router';
import { TenantStateService } from '@app/core/services/tenant-state.service';
import { ApiKeyPanelMode, ApiKeyPanelState, ApiKeyStoreService } from '@app/core/state/api-key-store.service';
import { AuthStore } from '@app/core/state/auth.store';
import { AppSettingsStore } from '@app/core/state/app-settings-store/app-settings-store';
import { ToastServicev2 } from '@app/shared/toast/services';
import { APP_ROUTES } from '@modules/navigation/models';

@Component({
    selector: 'sbpro-layout-user-simple',
    templateUrl: './layout-user-simple.component.html',
    styleUrls: ['./layout-user-simple.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LayoutUserSimpleComponent {
    readonly documentProcessingTooltip = 'Document Processing';
    readonly documentProcessingAriaLabel = 'Go to Document Processing';
    readonly apiKeyState$ = this.apiKeyStore.apiKeyState$;
    readonly apiKeyPanelState$ = this.apiKeyStore.panelState$;
    apiKeyInputValue = '';
    apiKeySubmitted = false;

    readonly tenantDisplayName = computed(
        () =>
            this.authStore.userInfoData()?.clientDisplayName ||
            this.tenantStateService.tenantName() ||
            this.authStore.getClientName() ||
            'Workspace'
    );

    constructor(
        public readonly appSettingsStore: AppSettingsStore,
        private readonly authStore: AuthStore,
        private readonly tenantStateService: TenantStateService,
        private readonly router: Router,
        private readonly apiKeyStore: ApiKeyStoreService,
        private readonly toastService: ToastServicev2
    ) {}

    goToDocumentProcessing(): void {
        this.router.navigateByUrl(APP_ROUTES.INDIVIDUAL_TEST_PAGE);
    }

    openApiKeyPanel(): void {
        this.resetApiKeyPanelForm();
        this.apiKeyStore.openPanel();
    }

    closeApiKeyPanel(): void {
        this.apiKeyStore.closePanel();
        this.resetApiKeyPanelForm();
    }

    enterApiKeyEditMode(): void {
        this.resetApiKeyPanelForm();
        this.apiKeyStore.openPanel('edit');
    }

    onApiKeyInputChange(value: string): void {
        this.apiKeyInputValue = value;
        if (value.trim()) {
            this.apiKeySubmitted = false;
        }
    }

    saveApiKey(panelState: ApiKeyPanelState): void {
        this.apiKeySubmitted = true;
        const apiKey = this.apiKeyInputValue.trim();

        if (!apiKey) {
            return;
        }

        this.apiKeyStore.setApiKey(apiKey);
        this.apiKeyStore.closePanel();
        this.resetApiKeyPanelForm();

        const message =
            panelState.mode === 'edit'
                ? 'API key updated successfully.'
                : 'API key saved successfully.';
        this.toastService.showSuccess('API Key', message);
    }

    isApiKeyFormMode(mode: ApiKeyPanelMode): boolean {
        return mode === 'setup' || mode === 'edit';
    }

    isDocumentProcessingActive(): boolean {
        return this.router.url.startsWith(APP_ROUTES.INDIVIDUAL_TEST_PAGE);
    }

    private resetApiKeyPanelForm(): void {
        this.apiKeyInputValue = '';
        this.apiKeySubmitted = false;
    }
}
